import { useEffect, useRef, useState } from "react";
import {
  isSupportedImageType,
  SUPPORTED_IMAGE_TYPES,
} from "../hooks/useImageDropZone";
import { useLatestPick } from "../hooks/useLatestPick";
import { downscaleImageFile } from "../lib/image-input";

/**
 * Selected file + object-URL preview, cleared when the host modal closes so
 * the object URL is revoked while the modal returns null but stays mounted
 * (which also guarantees a fresh selection on the next open).
 */
export function useImageFileSelection(isOpen: boolean) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) setFile(null);
  }, [isOpen]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return { file, setFile, previewUrl };
}

function ImageFilePicker({
  file,
  previewUrl,
  onSelect,
}: {
  file: File | null;
  previewUrl: string | null;
  onSelect: (file: File | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  // The picker unmounts when the host modal closes, but the modal keeps the
  // selection state, so a pick landing after that would reappear on reopen.
  const latestPick = useLatestPick();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected && isSupportedImageType(selected.type)) {
      const isLatest = latestPick.begin();
      // Clear the previous file while the new one is prepared, so the host's
      // primary action cannot submit the old image in the meantime.
      onSelect(null);
      downscaleImageFile(selected).then((scaled) => {
        if (isLatest()) onSelect(scaled);
      });
    }
    // Allow re-selecting the same file after clearing/replacing.
    e.target.value = "";
  };

  return (
    <div className="mt-5">
      <input
        ref={inputRef}
        type="file"
        accept={SUPPORTED_IMAGE_TYPES.join(",")}
        onChange={handleChange}
        className="hidden"
        aria-label="Upload asset image"
      />
      {file && previewUrl ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          aria-label="Change image"
          className="mx-auto block"
        >
          <img
            src={previewUrl}
            alt={file.name}
            className="aspect-square w-60 rounded-sm object-cover"
          />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="mx-auto flex h-11 items-center justify-center rounded-lg border border-[var(--Buttons-Secondary-Button---Stroke)] bg-[var(--Buttons-Secondary-Button---Fill)] px-3 py-2 text-sm font-semibold text-[var(--text-primary)] transition-colors hover:bg-[rgba(60,124,165,0.4)]"
        >
          Upload Image
        </button>
      )}
    </div>
  );
}

/** File picker + name field shared by the image-asset upload modals. */
export function ImageAssetUploadFields({
  file,
  previewUrl,
  onSelectFile,
  name,
  onNameChange,
  onSubmit,
}: {
  file: File | null;
  previewUrl: string | null;
  /** Called with null when a pick starts, then with the prepared file. */
  onSelectFile: (file: File | null) => void;
  name: string;
  onNameChange: (name: string) => void;
  /** Fired when Enter is pressed in the name field (e.g. to trigger the
   *  host modal's primary action). */
  onSubmit?: () => void;
}) {
  return (
    <>
      <ImageFilePicker
        file={file}
        previewUrl={previewUrl}
        onSelect={onSelectFile}
      />

      <div className="mt-5">
        <label
          htmlFor="image-asset-name"
          className="text-sm font-bold text-white"
        >
          Name
        </label>
        <input
          id="image-asset-name"
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSubmit?.();
            }
          }}
          placeholder="Name this asset"
          className="mt-2 w-full rounded-lg border border-black/25 bg-[var(--bg-field)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none placeholder-[var(--text-secondary)] focus:ring-2 focus:ring-white/25"
        />
      </div>
    </>
  );
}
